import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import NumberField from "../../../components/number-field";
import AutocompleteWithPagination from "../../../components/autocomplete-with-pagination";
import { wmsService } from "../../../utils/services/wmsProvider";
import { channelInventoryService } from "../../../utils/services/channelInventoryService";
import { clientConfigService } from "../../../utils/services/clientConfigService";
import { MONTHS } from "../utils";

// Same defaults as the Updated Forecast page (planning-forecast-new).
const LEAD_TIME_CONFIG_KEY = "forecast_lead_time_days";
const DEFAULT_LEAD_TIME_DAYS = 21;
const HISTORY_MAX_MONTHS = 36;

const monthKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const earliestHistoryMonth = (now = new Date()) => {
  const d = new Date(now.getFullYear(), now.getMonth() - HISTORY_MAX_MONTHS, 1);
  if (now.getDate() > 1) d.setMonth(d.getMonth() + 1);
  return monthKey(d);
};

/** "about 4 min" from seconds. */
export const fmtDuration = (secs) => {
  const s = Math.max(0, Math.round(Number(secs) || 0));
  if (s < 90) return `about ${Math.max(1, Math.round(s / 10) * 10)} sec`;
  const m = Math.round(s / 60);
  if (m < 90) return `about ${m} min`;
  return `about ${Math.round((m / 60) * 10) / 10} h`;
};

/**
 * Forecast parameters for a demand plan category. "Forecast By" is fixed to
 * Category; everything else can be changed. The forecast covers every SKU of
 * the category for the open months of the plan year (from this month to Dec).
 */
export default function RunForecastDialog({
  open,
  onClose,
  onSubmit, // async (params) => void
  categoryName,
  skuHint = null, // number of SKUs, when known
  clientId,
  planYear,
}) {
  const [leadTimeDays, setLeadTimeDays] = useState(DEFAULT_LEAD_TIME_DAYS);
  const [safetyStockPct, setSafetyStockPct] = useState(10);
  const [minCoverageDays, setMinCoverageDays] = useState(0);
  const [minOrderQty, setMinOrderQty] = useState("");
  const [discount, setDiscount] = useState(0);
  const [warehouses, setWarehouses] = useState([]);
  const [channels, setChannels] = useState([]);
  const [includeLostSales, setIncludeLostSales] = useState(true);
  // New SKUs: estimate the months before their first sale (PM, 2026-10-16).
  const [salesFill, setSalesFill] = useState(true);
  // Automatic email after the run is off for now (⋮ › Email Excel instead).
  // const [emailReport, setEmailReport] = useState(true);
  const [historyFrom, setHistoryFrom] = useState(() => earliestHistoryMonth());
  const [submitting, setSubmitting] = useState(false);

  // Client's default lead time, applied only while the field holds the default.
  useEffect(() => {
    if (!open || !clientId) return undefined;
    let cancelled = false;
    clientConfigService
      .fetch(clientId, 1, 0, LEAD_TIME_CONFIG_KEY)
      .then((res) => {
        const v = Number((res?.data || res || [])[0]?.value);
        if (!cancelled && Number.isFinite(v) && v >= 0) {
          setLeadTimeDays((cur) => (cur === DEFAULT_LEAD_TIME_DAYS ? v : cur));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, clientId]);

  const now = new Date();
  const nowYear = now.getFullYear();
  const firstMonth = planYear > nowYear ? 0 : now.getMonth();
  const periodLabel =
    planYear < nowYear
      ? "—"
      : `${MONTHS[firstMonth]}–Dec ${planYear}` +
        (planYear > nowYear
          ? ` (forecast starts ${MONTHS[now.getMonth()]} ${nowYear})`
          : "");

  const handleRun = async () => {
    setSubmitting(true);
    try {
      await onSubmit({
        lead_time_days: Number(leadTimeDays) || 0,
        safety_stock_percentage: Number(safetyStockPct) || 0,
        min_coverage_days: Number(minCoverageDays) || 0,
        ...(minOrderQty !== "" && minOrderQty != null
          ? { min_order_qty: Number(minOrderQty) || 0 }
          : {}),
        discount: Number(discount) || 0,
        ...(warehouses.length && {
          warehouse: warehouses.map((w) => w?._id || w).filter(Boolean),
        }),
        ...(channels.length && {
          channel: channels.map((c) => c?.name || c?._id || c).filter(Boolean),
        }),
        ...(includeLostSales && {
          include_lost_sales: true,
          search_window_days: 180,
        }),
        ...(historyFrom && { history_start: historyFrom }),
        ...(salesFill && { sales_fill: true }),
        // email_report: emailReport,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const grid = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
    gap: 1.75,
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>
        Run forecast · {categoryName}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.75, pt: "8px !important" }}>
        <Box sx={grid}>
          <TextField label="Forecast By" value="Category" size="small" disabled fullWidth />
          <TextField
            label="Months forecast"
            value={`Monthly · ${periodLabel}`}
            size="small"
            disabled
            fullWidth
          />
        </Box>

        <Box sx={grid}>
          <AutocompleteWithPagination
            multiple
            selectedItem={warehouses}
            onDataChange={(val) => setWarehouses(Array.isArray(val) ? val : [])}
            customLabel="Warehouses (optional)"
            optionKey={(item) => item?.name || item?.warehouse_code || item?.code || ""}
            apiEndPoint={(limit, skip, search) =>
              wmsService.fetch(clientId, limit, skip, undefined, search)
            }
            disabled={submitting}
            width="100%"
          />
          <AutocompleteWithPagination
            multiple
            selectedItem={channels}
            onDataChange={(val) => setChannels(Array.isArray(val) ? val : [])}
            customLabel="Channels (optional)"
            optionKey="name"
            apiEndPoint={(limit, skip, search) =>
              channelInventoryService.fetchSourceChannels(search, clientId, limit || 15, skip || 0)
            }
            disabled={submitting}
            width="100%"
          />
        </Box>

        <Box sx={grid}>
          <NumberField
            label="Lead Time (days)"
            value={leadTimeDays}
            onChange={(val) => setLeadTimeDays(val === "" ? "" : Math.max(0, Number(val)))}
            inputProps={{ min: 0, step: 1 }}
            fullWidth
            size="small"
            disabled={submitting}
          />
          <NumberField
            label="Safety Stock (% of on hand)"
            value={safetyStockPct}
            onChange={(val) => setSafetyStockPct(val === "" ? "" : Math.max(0, Number(val)))}
            inputProps={{ min: 0, step: 1 }}
            fullWidth
            size="small"
            disabled={submitting}
          />
          <NumberField
            label="Min Coverage (days)"
            value={minCoverageDays}
            onChange={(val) => setMinCoverageDays(val === "" ? "" : Math.max(0, Number(val)))}
            inputProps={{ min: 0, step: 1 }}
            fullWidth
            size="small"
            disabled={submitting}
          />
          <NumberField
            label="Min Order Qty (blank = SKU's own)"
            value={minOrderQty}
            onChange={(val) => setMinOrderQty(val === "" ? "" : Math.max(0, Number(val)))}
            inputProps={{ min: 0, step: 1 }}
            fullWidth
            size="small"
            disabled={submitting}
          />
          <NumberField
            label="Promo Discount (%)"
            value={discount}
            onChange={(val) => setDiscount(val === "" ? "" : Math.min(100, Math.max(0, Number(val))))}
            inputProps={{ min: 0, max: 100, step: 1 }}
            fullWidth
            size="small"
            disabled={submitting}
          />
          <TextField
            label="Sales history from"
            type="month"
            value={historyFrom}
            onChange={(e) => setHistoryFrom(e.target.value)}
            inputProps={{ min: earliestHistoryMonth() }}
            size="small"
            fullWidth
            disabled={submitting}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={includeLostSales}
              onChange={(e) => setIncludeLostSales(e.target.checked)}
              disabled={submitting}
            />
          }
          label={<Typography sx={{ fontSize: 13 }}>Include lost sales (stock-outs)</Typography>}
        />
        <FormControlLabel
          sx={{ mt: -1 }}
          control={
            <Switch
              checked={salesFill}
              onChange={(e) => setSalesFill(e.target.checked)}
              disabled={submitting}
            />
          }
          label={
            <Typography sx={{ fontSize: 13 }}>
              Sales fill for new SKUs{" "}
              <span style={{ color: "#888" }}>
                (estimate months before a SKU's first sale from its sibling SKUs, else the category)
              </span>
            </Typography>
          }
        />
        {/* Automatic email is off for now — planners use ⋮ › Email Excel.
        <FormControlLabel
          sx={{ mt: -1 }}
          control={
            <Switch
              checked={emailReport}
              onChange={(e) => setEmailReport(e.target.checked)}
              disabled={submitting}
            />
          }
          label={<Typography sx={{ fontSize: 13 }}>Email me the Excel when it's ready</Typography>}
        />
        */}

        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          Every SKU in {categoryName}
          {skuHint ? ` (${skuHint} SKUs)` : ""} is forecast in the background and
          rolled up to product and category. You'll see how long it should take
          once it starts; you can keep planning meanwhile. Editing the plan never
          needs a new run.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleRun}
          disabled={submitting || planYear < nowYear}
          startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
        >
          Run Forecast
        </Button>
      </DialogActions>
    </Dialog>
  );
}
